import React, { useState, useEffect, useRef } from "react";
import "./ChatWindow.css";
import X from "./X.png";
import placeholder from "./../../assets/placeholder-chat.png";
import ReconnectingWebSocket from "reconnecting-websocket";
import { marked } from "marked";
import { Smile } from "lucide-react";
import EmojiPicker, { EmojiClickData } from "emoji-picker-react"; // Emoji picker functionality
import Hint from "./hint";
import purify from "dompurify";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { Button } from "@/components/ui/button";
import { parse } from "path";
import { v4 as uuidv4 } from "uuid";


const generateSessionId = () => {
  return Math.random().toString(36).substr(2, 9);
};

interface ChatWindowProps {
  onClose: () => void;
  selectedchatAgent: string;
  name: string;
  chatHints: any;
  chatIcon: any;
  agentId: string;
}

const ChatWindow: React.FC<ChatWindowProps> = ({
  onClose,
  selectedchatAgent,
  name,
  chatHints,
  chatIcon,
  agentId
}) => {
  // Destructure props here
  const { executeRecaptcha } = useGoogleReCaptcha();

  const chatServerUrl: string = process.env.REACT_APP_CHAT_SERVER_URL || "";
  const chatApiKey: string = process.env.REACT_APP_CHAT_API_KEY || "";
  const [messages, setMessages] = useState<
    { text: string; sender: string; parsedObj?: any }[]
  >([]);
  const [input, setInput] = useState("");
  const [sessionId] = useState(generateSessionId);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const wsRef = useRef<ReconnectingWebSocket | null>(null);
  const [typing, setTyping] = useState(false);

  function extractJsonObjectFromString(input: any) {
    // Try PRODUCT_DATA_JSON code block first
    const marker = "PRODUCT_DATA_JSON";
    const markerIdx = input.indexOf(marker);
    if (markerIdx !== -1) {
      const codeBlockStart = input.indexOf("```json", markerIdx);
      if (codeBlockStart !== -1) {
        const jsonStart = input.indexOf("{", codeBlockStart);
        const codeBlockEnd = input.indexOf("```", jsonStart);
        if (jsonStart !== -1 && codeBlockEnd !== -1) {
          const jsonString = input.slice(jsonStart, codeBlockEnd);
          let json = null;
          try {
            json = JSON.parse(jsonString);
          } catch (e) { }
          const text = (
            input.slice(0, markerIdx) + input.slice(codeBlockEnd + 3)
          )
            .replace(/\s+/g, " ")
            .trim();
          return { json, text };
        }
      }
    }

    // Try parsedData marker with direct JSON
    const parsedDataIdx = input.indexOf("parsedData");
    if (parsedDataIdx !== -1) {
      const braceIdx = input.indexOf("{", parsedDataIdx);
      if (braceIdx !== -1) {
        let openBraces = 0;
        let endIdx = -1;
        for (let i = braceIdx; i < input.length; i++) {
          if (input[i] === "{") openBraces++;
          if (input[i] === "}") openBraces--;
          if (openBraces === 0) {
            endIdx = i;
            break;
          }
        }
        if (endIdx !== -1) {
          const jsonString = input.slice(braceIdx, endIdx + 1);
          let json = null;
          try {
            json = JSON.parse(jsonString);
          } catch (e) { }
          const text = (input.slice(0, parsedDataIdx) + input.slice(endIdx + 1))
            .replace(/\s+/g, " ")
            .trim();
          return { json, text };
        }
      }
    }

    // Fallback: no JSON found
    return { json: null, text: input };
  }

  useEffect(() => {
    localStorage.removeItem("chatMessages");
    setMessages([]);
    scrollToBottom();
  }, []);

  useEffect(() => {
    const userId = uuidv4()

    const wsUrl = `${chatServerUrl}/v2/chat/INTERNAL_CHAT/nuplay/${sessionId}?user_id=${userId}&api_key=${chatApiKey}&agent_id=${agentId}`;

    wsRef.current = new ReconnectingWebSocket(wsUrl);

    wsRef.current.onopen = () => {
      setMessages([]);
      scrollToBottom();
    };

    wsRef.current.onmessage = (event) => {
      const response = JSON.parse(event.data);
      if (response.response_type === "typing") {
        setTyping(true);
      }
      if (response.response_type === "response") {
        // const botMessage = { text: response.content, sender: "bot" };
        let { json: parsedObj, text: updatedContent } =
          extractJsonObjectFromString(response.content);
        const botMessage = { text: updatedContent, sender: "bot", parsedObj };

        console.log("botMessage", botMessage);
        setMessages((prevMessages) => [...prevMessages, botMessage]);
        setTyping(false);
      }
    };

    return () => {
      wsRef.current?.close();
    };
  }, [sessionId, selectedchatAgent]);

  interface Product {
    imageUrl?: string;
    productName?: string;
    description?: string;
    price?: string | number;
    productUrl?: string;
  }

  const ProductCard: React.FC<{ product: Product }> = ({ product }) => {
    return (
      <div className="max-w-[150px] mb-6 h-[175px] rounded-2xl border border-gray-200 bg-white pt-2 pl-2 pr-2 pb-4 shadow hover:shadow-lg transition">
        <a href={product.productUrl} target="_blank" rel="noreferrer">
          <div className="flex justify-center">
            <img
              src={product.imageUrl || "/placeholder-image.jpg"}
              alt={product.productName || "Product"}
              width="150px"
              height="150px"
              className="rounded-lg object-contain"
            />
          </div>

          <h3 className="line-clamp-1 mt-3 text-lg font-semibold text-gray-800">
            {product.productName}
          </h3>
          <p className="mt-1 text-sm text-gray-500 line-clamp-3 text-left">
            {product.description || "No description available."}
          </p>
        </a>
      </div>
    );
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSend = async () => {
    // if (!executeRecaptcha) {
    //   return;
    // }

    // const token = await executeRecaptcha("send_message");

    if (input.trim()) {
      const userMessage = { text: input.trim(), sender: "user" };
      setMessages([...messages, userMessage]);

      // const requestPayload = {
      //   interaction_type: "response_required",
      //   transcript: [{ content: input.trim(), role: "user" }]
      //   // captcha_token: token
      // };

      const requestPayload = {
        text: input.trim(),
      };

      wsRef.current?.send(JSON.stringify(requestPayload));
      setInput("");
      scrollToBottom();
    }
  };

  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  const onEmojiClick = (emojiObject: EmojiClickData) => {
    setInput((prevInput) => prevInput + emojiObject.emoji); // Append the selected emoji to the input
  };

  // Add this new function to handle clicks outside the chatbot
  const handleOutsideClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div className="modal-overlay" onClick={handleOutsideClick}>
      <div className="category">
        <div className="topheader">{name}</div>
        {/* Changed to match CallWindow modal style */}
        <div className="chat-window-container">
          <div className="chat-inside-container">
            {" "}
            {/* Changed class name for consistent naming */}
            <div className="header">
              <div className="Ai-Chat">AI Chat</div>
              <Button
                className="text-md text-white border-none rounded-3xl h-10"
                variant="destructive"
                onClick={onClose}
              >
                End Chat
              </Button>
            </div>
            <div className="messages">
              {messages.map((msg, index) => {
                console.log("Rendering message:", msg);
                return (
                  <div key={index}>
                    <div className={`message-container ${msg.sender}`}>
                      {msg.sender === "bot" && (
                        <>
                          <img
                            src={chatIcon ? chatIcon : X}
                            className="user-icon"
                            alt="Assistant"
                          />
                          <div
                            className={`message ${msg.sender}`}
                            dangerouslySetInnerHTML={{
                              __html: purify.sanitize(
                                marked(msg.text.trim()) as string
                              )
                            }}
                          />
                        </>
                      )}

                      {msg.sender === "user" && (
                        <>
                          <div className={`message ${msg.sender}`}>
                            {msg.text.trim()}
                          </div>
                          <img
                            className="user-icon"
                            src={placeholder}
                            alt="you"
                          />
                        </>
                      )}
                    </div>
                    {chatHints.length > 0 &&
                      chatHints.map((hint: any, index: any) => (
                        <Hint
                          key={index}
                          triggers={hint.triggers}
                          text={msg.text}
                          hint={hint.hint}
                        />
                      ))}
                    {/* Render product cards if parsedObj exists and has products */}
                    {msg.parsedObj &&
                      msg.parsedObj.data &&
                      Array.isArray(msg.parsedObj.data.products) &&
                      msg.parsedObj.data.products.length > 0 && (
                        <div className="ml-[40px] flex gap-4 mt-4 overflow-x-auto">
                          {msg.parsedObj.data.products.map(
                            (product: Product, idx: number) => (
                              <div key={idx} className="flex-shrink-0">
                                <ProductCard product={product} />
                              </div>
                            )
                          )}
                        </div>
                      )}
                  </div>
                );
              })}

              {/* Display typing indicator when the agent is typing */}
              {typing && (
                <div className="typing-indicator">
                  <p>Nuplay Agent is typing...</p>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
            <div className="input-column">
              <div className="input-box">
                <textarea
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyPress={async (e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      await handleSend();
                    }
                  }}
                  placeholder="Write your message..."
                  className="input-box-input"
                  rows={1}
                  onInput={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
                    e.target.style.height = "auto";
                    e.target.style.height = e.target.scrollHeight + "px";
                  }}
                />
                <div className="input-icons">
                  {/* Emoji button to toggle emoji picker */}
                  <button
                    className="emoji-button"
                    onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                  >
                    <Smile
                      size={30}
                      color="#ffffff"
                      style={{
                        backgroundColor: "#c1c2cc",
                        borderRadius: "50%"
                      }}
                    />{" "}
                    {/* Emoji icon with background color */}
                  </button>
                </div>

                <button className="send" onClick={handleSend}>
                  <span className="send-icon">&#10148;</span>{" "}
                  {/* This is the arrow symbol */}
                </button>

                {/* Emoji picker should be inside input-box but positioned relative */}
                {showEmojiPicker && (
                  <div className="emoji-picker">
                    <EmojiPicker onEmojiClick={onEmojiClick} />
                  </div>
                )}
              </div>
            </div>
            <span className="opacity-30 mb-4 lg:mt-0 mt-2 px-4 text-center">
              This AI agent is a demo and may not provide fully accurate
              responses or take any actions.
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatWindow;
